import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { integrationsConfigApi, IntegrationConfigListItem, IntegrationType } from '../api/integrationsConfig';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { usersApi, DbUser } from '../api/client';
import { tokens } from '../theme/tokens';

type FormState = Record<
  IntegrationType,
  {
    name: string;
    baseUrl: string;
    email: string;
    workspaceId: string;
    spaceKey: string;
    apiToken: string;
    apiKey: string;
    revealApiToken: boolean;
    revealApiKey: boolean;
    loadingReveal: boolean;
  }
>;

const DEFAULTS: FormState = {
  JIRA: { name: 'Jira', baseUrl: '', email: '', workspaceId: '', spaceKey: '', apiToken: '', apiKey: '', revealApiToken: false, revealApiKey: false, loadingReveal: false },
  JELLYFISH: { name: 'Jellyfish', baseUrl: '', email: '', workspaceId: '', spaceKey: '', apiToken: '', apiKey: '', revealApiToken: false, revealApiKey: false, loadingReveal: false },
  BONUSLY: { name: 'Bonusly', baseUrl: '', email: '', workspaceId: '', spaceKey: '', apiToken: '', apiKey: '', revealApiToken: false, revealApiKey: false, loadingReveal: false },
};

const Settings: React.FC = () => {
  const { session, dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'settings');
  const [rows, setRows] = useState<IntegrationConfigListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<IntegrationType | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [importing, setImporting] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [me, setMe] = useState<DbUser | null>(null);
  const [username, setUsername] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load current DB user for username preference
      try {
        const meRes = await usersApi.me();
        const mine = (meRes.data || null) as DbUser | null;
        setMe(mine);
        const email = String(session?.user?.email || '').toLowerCase();
        const defaultUsername = email ? email.split('@')[0] : '';
        setUsername(String(mine?.username || defaultUsername));
      } catch {
        // ignore; profile editing will be disabled
      }

      // Only load integrations for admins
      if (canEdit) {
        const res = await integrationsConfigApi.list();
        const list = res.data || [];
        setRows(list);

        setForm((prev) => {
          const next: FormState = { ...prev };
          (['JIRA'] as IntegrationType[]).forEach((t) => {
            const found = list.find((r) => r.type === t);
            next[t] = {
              ...next[t],
              name: found?.name || next[t].name,
              baseUrl: found?.baseUrl || '',
              email: found?.email || '',
              workspaceId: found?.workspaceId || '',
              spaceKey: found?.spaceKey || '',
              apiToken: found?.apiTokenMasked || '',
              apiKey: found?.apiKeyMasked || '',
            };
          });
          return next;
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const active = rows.filter((r) => String(r.status || '').toUpperCase() === 'ACTIVE').length;
    const configured = rows.filter((r) => r.hasApiToken || r.hasApiKey).length;
    return { integrations: rows.length, active, configured };
  }, [rows]);

  const save = async (type: IntegrationType) => {
    try {
      setSaving(type);
      const f = form[type];
      await integrationsConfigApi.upsert(type, {
        name: f.name || undefined,
        baseUrl: f.baseUrl || undefined,
        email: f.email || undefined,
        workspaceId: f.workspaceId || undefined,
        spaceKey: f.spaceKey || undefined,
        apiToken: f.apiToken && f.apiToken.includes('••••') ? undefined : f.apiToken,
        apiKey: f.apiKey && f.apiKey.includes('••••') ? undefined : f.apiKey,
        status: 'ACTIVE',
      });
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const importFromEnv = async () => {
    try {
      setImporting(true);
      setError(null);
      await integrationsConfigApi.importEnv();
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reveal = async (type: IntegrationType) => {
    setForm((prev) => ({ ...prev, [type]: { ...prev[type], loadingReveal: true } }));
    try {
      const res = await integrationsConfigApi.reveal(type);
      const data = res.data || {};
      setForm((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          apiToken: data.apiToken || '',
          apiKey: data.apiKey || '',
          revealApiToken: true,
          revealApiKey: true,
          loadingReveal: false,
        },
      }));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Reveal failed');
      setForm((prev) => ({ ...prev, [type]: { ...prev[type], loadingReveal: false } }));
    }
  };

  const IntegrationCard: React.FC<{ type: IntegrationType; title: string; subtitle: string; accent: string }> = ({ type, title, subtitle, accent }) => {
    const f = form[type];
    const row = rows.find((r) => r.type === type);
    return (
      <TableCard
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {f.loadingReveal ? <CircularProgress size={18} /> : null}
            <Button size="small" variant="outlined" onClick={() => reveal(type)} sx={{ borderRadius: `${tokens.radius.md}px` }}>
              Reveal
            </Button>
            <Button size="small" variant="contained" onClick={() => save(type)} disabled={saving === type} sx={{ borderRadius: `${tokens.radius.md}px`, boxShadow: 'none' }}>
              {saving === type ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <Box sx={{ borderLeft: `2px solid ${accent}`, pl: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField label="Name" value={f.name} onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], name: e.target.value } }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Base URL" value={f.baseUrl} onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], baseUrl: e.target.value } }))} fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Email / Username" value={f.email} onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], email: e.target.value } }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Workspace / Space / Org" value={f.workspaceId} onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], workspaceId: e.target.value } }))} fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Space Key (Confluence)" value={f.spaceKey} onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], spaceKey: e.target.value } }))} fullWidth />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="API Token"
                value={f.apiToken}
                onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], apiToken: e.target.value } }))}
                type={f.revealApiToken ? 'text' : 'password'}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setForm((p) => ({ ...p, [type]: { ...p[type], revealApiToken: !p[type].revealApiToken } }))}
                      >
                        {f.revealApiToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={row?.apiTokenMasked ? `Stored: ${row.apiTokenMasked}` : 'Not set'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="API Key / Webhook"
                value={f.apiKey}
                onChange={(e) => setForm((p) => ({ ...p, [type]: { ...p[type], apiKey: e.target.value } }))}
                type={f.revealApiKey ? 'text' : 'password'}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setForm((p) => ({ ...p, [type]: { ...p[type], revealApiKey: !p[type].revealApiKey } }))}
                      >
                        {f.revealApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={row?.apiKeyMasked ? `Stored: ${row.apiKeyMasked}` : 'Not set'}
              />
            </Grid>
          </Grid>
        </Box>
      </TableCard>
    );
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Settings"
        subtitle={canEdit ? 'Configure integrations and platform preferences.' : 'Manage your profile.'}
        actions={
          canEdit ? (
            <Button
              size="small"
              variant="outlined"
              onClick={importFromEnv}
              disabled={importing || loading}
              sx={{ borderRadius: `${tokens.radius.md}px` }}
            >
              {importing ? 'Importing…' : 'Import from server env'}
            </Button>
          ) : undefined
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableCard
        title="Profile"
        subtitle="Your username is shown across the app. Default is your email prefix."
        actions={
          <Button
            size="small"
            variant="contained"
            disabled={profileSaving || !me?.id || !username.trim()}
            onClick={async () => {
              try {
                setProfileSaving(true);
                setError(null);
                await usersApi.updateMe({ username: username.trim() } as any);
                await refresh();
              } catch (e: any) {
                setError(e?.response?.data?.message || e?.message || 'Failed to update username');
              } finally {
                setProfileSaving(false);
              }
            }}
            sx={{ borderRadius: `${tokens.radius.md}px` }}
          >
            {profileSaving ? 'Saving…' : 'Save username'}
          </Button>
        }
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
          <TextField label="Username" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth />
          <TextField label="Email" value={me?.email || session?.user?.email || ''} fullWidth disabled />
        </Box>
      </TableCard>

      {canEdit && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <StatCard title="Integrations" value={totals.integrations} subtitle="Saved configs" accent={tokens.color.accent} />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard title="Configured" value={totals.configured} subtitle="Has token/key" accent={tokens.color.success} />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard title="Active" value={totals.active} subtitle="Status = ACTIVE" accent={tokens.color.accent} />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {rows.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No integration configs are stored in the database yet. Click <b>Import from server env</b> to pull keys from the backend
                  `.env`, or enter them below and hit Save.
                </Alert>
              ) : null}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <IntegrationCard type="JIRA" title="Jira" subtitle="Project boards and issue sync." accent={tokens.color.accent} />
                </Grid>
              </Grid>
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Note: If you set `INTEGRATIONS_ENCRYPTION_KEY` (base64 32 bytes) on the backend, secrets are stored encrypted at rest.
          </Typography>
        </>
      )}
      </Container>
    </PageLoader>
  );
};

export default Settings;


